import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { PROCESS_STEPS, PROCESS_COLORS } from "@/lib/data";
import { Reveal } from "@/components/Reveal";

const R = 150;
const CX = 200;
const CY = 200;
const nodePos = (i) => {
  const a = (i / PROCESS_STEPS.length) * 2 * Math.PI - Math.PI / 2;
  return { x: CX + R * Math.cos(a), y: CY + R * Math.sin(a) };
};

export default function HowWeWork() {
  const [active, setActive] = useState(0);
  const paused = useRef(false);

  useEffect(() => {
    const t = setInterval(() => {
      if (!paused.current) setActive((a) => (a + 1) % PROCESS_STEPS.length);
    }, 2600);
    return () => clearInterval(t);
  }, [active]);

  const step = PROCESS_STEPS[active];
  const color = PROCESS_COLORS[active];

  return (
    <section id="how-we-work" className="pt-24 pb-16 lg:pt-32 lg:pb-20 bg-white" data-testid="how-we-work-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="max-w-4xl mb-6">
          <Reveal>
            <div className="text-[11.5px] uppercase tracking-[0.22em] text-medis-teal font-semibold">How We Work</div>
            <h3 className="mt-3 font-display font-bold text-medis-navy text-2xl sm:text-3xl lg:text-[30px] leading-[1.15] tracking-tight">
              From requirement to long-term performance a clear 5-step process.
            </h3>
          </Reveal>
        </div>

        <div className="mt-8 bg-white overflow-hidden rounded-xl border border-medis-silver/25">
          <div className="h-[3px] bg-medis-silver/20">
            <motion.div
              key={active}
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 2.6, ease: "linear" }}
              style={{ height: "100%", background: color, borderRadius: "0 2px 2px 0" }}
              data-testid="process-progress-bar"
            />
          </div>

          <div className="p-6 lg:p-10 flex flex-col lg:flex-row items-center gap-10">
            <div className="flex-shrink-0 flex justify-center w-full lg:w-auto">
              <svg viewBox="0 0 400 400" style={{ overflow: "visible", width: "100%", maxWidth: 300 }} data-testid="process-orbit">
                <g style={{ transformOrigin: `${CX}px ${CY}px`, animation: "orbit 22s linear infinite" }}>
                  <circle cx={CX} cy="40" r="3" fill="#1D9E75" opacity=".3" />
                  <circle cx="360" cy={CY} r="2.5" fill="#185FA5" opacity=".2" />
                  <circle cx={CX} cy="360" r="3" fill="#1D9E75" opacity=".25" />
                  <circle cx="40" cy={CY} r="2.5" fill="#185FA5" opacity=".2" />
                </g>
                <g style={{ transformOrigin: `${CX}px ${CY}px`, animation: "counterOrbit 15s linear infinite" }}>
                  <circle cx="300" cy="68" r="2" fill="#1a9080" opacity=".2" />
                  <circle cx="332" cy="310" r="2" fill="#1a9080" opacity=".2" />
                  <circle cx="68" cy="310" r="2" fill="#185FA5" opacity=".15" />
                </g>
                <circle cx={CX} cy={CY} r={R} fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="5 8" />
                <circle
                  cx={CX}
                  cy={CY}
                  r={R}
                  fill="none"
                  stroke={color}
                  strokeWidth="3.5"
                  strokeDasharray={2 * Math.PI * R}
                  strokeDashoffset={2 * Math.PI * R * (1 - (active + 1) / PROCESS_STEPS.length)}
                  strokeLinecap="round"
                  transform={`rotate(-90 ${CX} ${CY})`}
                  style={{ transition: "stroke-dashoffset 0.6s cubic-bezier(0.4,0,0.2,1), stroke 0.4s" }}
                />
                <circle cx={CX} cy={CY} r="92" fill="white" />
                <circle cx={CX} cy={CY} r="86" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="0.5" />
                <text x={CX} y="195" textAnchor="middle" fontSize="40" fontWeight="700" fill={color} style={{ transition: "fill 0.3s" }}>
                  {step.num}
                </text>
                <text x={CX} y="221" textAnchor="middle" fontSize="11" fill="#94a3b8" letterSpacing="3.5">
                  {step.tag.toUpperCase()}
                </text>
                {PROCESS_STEPS.map((s, i) => {
                  const p = nodePos(i);
                  const isActive = i === active;
                  const isPast = i < active;
                  return (
                    <g
                      key={s.num}
                      style={{ cursor: "pointer" }}
                      onClick={() => setActive(i)}
                      onMouseEnter={() => (paused.current = true)}
                      onMouseLeave={() => (paused.current = false)}
                      data-testid={`process-node-${i}`}
                    >
                      {isActive && <circle cx={p.x} cy={p.y} r="30" fill={PROCESS_COLORS[i]} opacity="0.35" style={{ filter: "blur(6px)" }} />}
                      <circle
                        cx={p.x}
                        cy={p.y}
                        r={isActive ? 30 : 20}
                        fill={isActive ? PROCESS_COLORS[i] : isPast ? `${PROCESS_COLORS[i]}33` : "white"}
                        stroke={PROCESS_COLORS[i]}
                        strokeWidth={isActive ? 0 : 2.5}
                        style={{ transition: "r 0.35s cubic-bezier(0.4,0,0.2,1)" }}
                      />
                      <text
                        x={p.x}
                        y={p.y + 5}
                        textAnchor="middle"
                        fontSize={isActive ? "15" : "12"}
                        fontWeight={isActive ? "700" : "500"}
                        fill={isActive ? "white" : PROCESS_COLORS[i]}
                        style={{ pointerEvents: "none", transition: "font-size 0.35s" }}
                      >
                        {s.num}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>

            <div className="flex-1 min-w-0 text-left">
              <motion.div key={active} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                <div className="text-[11px] uppercase tracking-[0.2em] font-semibold mb-3" style={{ color }}>
                  {step.tag}
                </div>
                <div className="font-display font-bold text-[40px] leading-none mb-3" style={{ color }}>
                  {step.num}
                </div>
                <h4 className="font-display font-bold text-medis-navy text-[22px] leading-snug mb-3">{step.title}</h4>
                <p className="text-medis-gray text-[14.5px] leading-relaxed">{step.text}</p>
                <ul className="mt-5 flex flex-col gap-3">
                  {step.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 text-[13.5px] text-medis-gray">
                      <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: color }} />
                      {p}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
